import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { View } from 'react-native';
import { TWCAccessibleContent, TWCText } from '@components';
import moment from 'moment-timezone';
import { useRecoilValue } from 'recoil';

import { currentWeatherConditionsPageTestIDs } from '@constants';
import { helpers } from '@helpers';
import { useGetUserLocation, useNodeHandle } from '@hooks';
import { dayDateTimeState } from '@store';

import { CurrentWeatherHeaderTestIds, tPath } from './constants';
import { styles } from './styles';

export type CurrentWeatherHeaderProps = {
  nextFocusDown?: null;
};

export const CurrentWeatherHeader: FC<CurrentWeatherHeaderProps> = ({ nextFocusDown }) => {
  const { t } = useTranslation('translation', { keyPrefix: tPath });

  const { utcTime, timeZone } = useRecoilValue(dayDateTimeState);

  const { city } = useGetUserLocation() || {};

  const utcDate = helpers.TimeUtils.dateFromEpoch(utcTime);
  const tzAdjustedDate = moment(utcDate).tz(timeZone);
  const date = tzAdjustedDate?.format('MMMM D');
  const time = tzAdjustedDate?.format('h:mma').slice(0, -1);
  const accessibleTime = tzAdjustedDate?.format('h mm a');

  const handle = useNodeHandle(nextFocusDown);

  return (
    <TWCAccessibleContent
      accessibilityHint={t('accessibilityHint')}
      accessibilityLabel={t('accessibilityLabel', {
        city,
        date,
        time: accessibleTime,
      })}
      disabledScreenreaderFocus={nextFocusDown}
      nextFocusDown={handle}
      nextFocusLeft={null}
      nextFocusRight={null}
      nextFocusUp={null}
      testID={currentWeatherConditionsPageTestIDs.currentWeatherTopRow}
    >
      <View style={styles.container}>
        <View style={styles.topRow} testID={CurrentWeatherHeaderTestIds.city}>
          <TWCText
            importantForAccessibility="no"
            testID={currentWeatherConditionsPageTestIDs.currentWeatherHeaderLabel}
            variant="pageHeader"
          >
            {city}
          </TWCText>
        </View>
        <View style={styles.bottomRow}>
          <TWCText
            importantForAccessibility="no"
            testID={currentWeatherConditionsPageTestIDs.currentWeatherHeaderDateTime}
            variant="regularText"
          >
            {`${date}, ${time}`}
          </TWCText>
        </View>
      </View>
    </TWCAccessibleContent>
  );
};
